import type { LocationConfig } from './types';

const DEFAULT_THRESHOLDS = {
  greenAbovePct: 50,
  yellowAbovePct: 20,
  redBelowPct: 20,
  pulseBelowSec: 10
};

const LS_KEY = (slug: string) => `dd-timer:${slug}`;

export function resolveLocationSlug(): string {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
  if (!path) return 'default';
  return path.split('/')[0].toLowerCase();
}

export async function loadLocationConfig(slug: string): Promise<LocationConfig> {
  const tryFetch = async (s: string) => {
    const res = await fetch(`/locations/${s}.json`, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };
  let raw: any;
  try {
    raw = await tryFetch(slug);
  } catch {
    raw = await tryFetch('default');
  }

  const baseline = normalize(raw, slug);
  const override = loadLocalOverrides(slug);
  return override ? { ...baseline, ...override, slug } : baseline;
}

export function saveLocalOverrides(cfg: LocationConfig): void {
  const { slug, ...rest } = cfg;
  try { localStorage.setItem(LS_KEY(slug), JSON.stringify(rest)); } catch { /* quota */ }
}

export function clearLocalOverrides(slug: string): void {
  try { localStorage.removeItem(LS_KEY(slug)); } catch { /* ignore */ }
}

function loadLocalOverrides(slug: string): Partial<LocationConfig> | null {
  try {
    const raw = localStorage.getItem(LS_KEY(slug));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function normalize(raw: any, slug: string): LocationConfig {
  return {
    slug,
    location: {
      name: String(raw?.location?.name ?? 'Dill Dinkers'),
      sub: String(raw?.location?.sub ?? '')
    },
    theme: (raw.theme ?? 'navy') as LocationConfig['theme'],
    ticker: Array.isArray(raw.ticker) ? raw.ticker.map(String) : [],
    schedule: {
      loop: raw?.schedule?.loop !== false,
      intervals: Array.isArray(raw?.schedule?.intervals) && raw.schedule.intervals.length > 0
        ? raw.schedule.intervals.map((i: any) => ({
            label: String(i.label ?? 'INTERVAL'),
            durationSec: Math.max(1, parseInt(i.durationSec) || 60)
          }))
        : [{ label: 'PLAY', durationSec: 900 }]
    },
    sound: raw.sound !== false,
    colorThresholds: { ...DEFAULT_THRESHOLDS, ...(raw.colorThresholds ?? {}) }
  };
}
